/**
 * useCrossRefCandidates — ADR-0002 Fase 10.f.1.
 *
 * Lista los artifact templates publicados del catálogo del tenant junto
 * con sus secciones, para popular el dropdown "De otros documentos del
 * proyecto" del `ContextSectionsBlock`.
 *
 * Conceptualmente las cross-refs se resuelven contra el snapshot de un
 * proyecto en runtime (backend). En el designer del template NO hay
 * proyecto: aquí solo declaramos refs que apuntan a templates del
 * catálogo. El backend valida runtime que cada `tpl-x.section_id` exista
 * en el snapshot del proyecto consumidor (Doc 24 §1.8 + ADR-0002 D7).
 *
 * Convención del hook:
 *   - Filtra fuera el template que se está editando (`excludeTemplateKey`).
 *   - Solo artifact templates con status `published` — un template draft
 *     no debe poder ser referenciado todavía.
 *   - Mapea cada template a `AvailableCrossDoc` con sus secciones
 *     top-level (children recursivos no se exponen como cross-refs por
 *     ahora — la convención es referenciar la sección root nombrada).
 */

import { useQuery } from '@tanstack/react-query'

import {
  getCurrentVersion,
  listTemplates,
  type Template,
  type TemplateVersionWithDefinition,
} from '../api/templates'
import { useAccessToken } from '../auth/useAccessToken'
import type { AvailableCrossDoc } from '../components/templates/designer/ContextSectionsBlock'

interface I18nSection {
  section_id?: string
  title?: string
}

interface StructuralSection {
  section_id?: string
  type?: string
}

function extractCrossDoc(
  tpl: Template,
  version: TemplateVersionWithDefinition,
): AvailableCrossDoc {
  const def = (version.definition ?? {}) as Record<string, unknown>
  const i18nBlock = (def.i18n ?? {}) as Record<string, unknown>
  const esBlock = (i18nBlock.es ?? {}) as Record<string, unknown>
  const enBlock = (i18nBlock.en ?? {}) as Record<string, unknown>
  const esSections = Array.isArray(esBlock.sections)
    ? (esBlock.sections as I18nSection[])
    : []
  const enSections = Array.isArray(enBlock.sections)
    ? (enBlock.sections as I18nSection[])
    : []

  const sectionTitleById = new Map<string, { es: string; en: string }>()
  for (const s of esSections) {
    if (typeof s.section_id === 'string') {
      const prev = sectionTitleById.get(s.section_id) ?? {
        es: s.section_id,
        en: s.section_id,
      }
      sectionTitleById.set(s.section_id, {
        ...prev,
        es: typeof s.title === 'string' ? s.title : s.section_id,
      })
    }
  }
  for (const s of enSections) {
    if (typeof s.section_id === 'string') {
      const prev = sectionTitleById.get(s.section_id) ?? {
        es: s.section_id,
        en: s.section_id,
      }
      sectionTitleById.set(s.section_id, {
        ...prev,
        en: typeof s.title === 'string' ? s.title : s.section_id,
      })
    }
  }

  const structural = Array.isArray(def.sections)
    ? (def.sections as StructuralSection[])
    : []
  const sections = structural
    .filter((s): s is StructuralSection & { section_id: string } =>
      typeof s.section_id === 'string' && s.section_id.length > 0,
    )
    .map((s) => ({
      section_id: s.section_id,
      title: sectionTitleById.get(s.section_id) ?? {
        es: s.section_id,
        en: s.section_id,
      },
    }))

  return {
    template_key: tpl.template_key,
    // El catálogo PG no separa i18n del nombre del template (Template.name
    // es un solo string). Lo replicamos en ambos lados como fallback
    // razonable hasta que se modele el nombre bilingüe.
    template_name: { es: tpl.name, en: tpl.name },
    sections,
  }
}

export function useCrossRefCandidates(excludeTemplateKey: string | undefined) {
  const { accessToken } = useAccessToken()
  return useQuery<AvailableCrossDoc[]>({
    queryKey: [
      'templates',
      'cross-ref-candidates',
      excludeTemplateKey ?? null,
    ],
    queryFn: async () => {
      const all = await listTemplates(accessToken!, {
        template_type: 'artifact',
        template_status: 'published',
      })
      const filtered = excludeTemplateKey
        ? all.filter((t) => t.template_key !== excludeTemplateKey)
        : all
      if (filtered.length === 0) return []
      // Fetch en paralelo las definiciones de cada template publicado.
      const versions = await Promise.all(
        filtered.map((t) => getCurrentVersion(accessToken!, t.id)),
      )
      return filtered.map((tpl, idx) =>
        extractCrossDoc(tpl, versions[idx]!),
      )
    },
    enabled: Boolean(accessToken),
    staleTime: 60_000,
  })
}
